import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ru";

import {
    getSituationLabel,
    SITUATION_CLASS_UI,
} from "../../constants/notificationClasses";
import { formatAlertConfidence, getAlertPrimarySummary } from "../../utils/notificationMessage";

import "./doctor-notifications.css";

dayjs.extend(relativeTime);
dayjs.locale("ru");

const EVIDENCE_PREVIEW_LIMIT = 5;

/**
 * Карточка одного уведомления (список, dock, страница).
 * Клинический текст — из patient_summary_ru / doctor_alert_ru / evidence (API).
 */
export default function NotificationCard({
    alert,
    compact = false,
    onMarkRead,
    onOpenPatient,
    markingRead = false,
}) {
    const accent = SITUATION_CLASS_UI[alert.situation_class]?.accent ?? "neutral";
    const classLabel = getSituationLabel(alert.situation_class);
    const timeLabel = dayjs(alert.created_at).fromNow();

    const primarySummary = getAlertPrimarySummary(alert);
    const confidenceLabel = formatAlertConfidence(alert.confidence);
    const doctorNote = alert.doctor_alert_ru?.trim() || null;
    const evidence = Array.isArray(alert.evidence)
        ? alert.evidence.filter((item) => String(item).trim()).slice(0, EVIDENCE_PREVIEW_LIMIT)
        : [];
    const showLegacyMessageOnly =
        !alert.patient_summary_ru?.trim() &&
        alert.message?.trim() &&
        alert.message.trim() !== primarySummary;

    return (
        <article
            className={`doctor-notification-card doctor-notification-card--${accent} ${compact ? "doctor-notification-card--compact" : ""} ${alert.is_read ? "doctor-notification-card--read" : ""}`}
        >
            <div className="doctor-notification-card__header">
                <span className={`doctor-notification-badge doctor-notification-badge--${accent}`}>
                    {alert.situation_class}
                </span>
                <span className="doctor-notification-card__meta">{classLabel}</span>
                {!compact && confidenceLabel && (
                    <span className="doctor-notification-card__confidence" title="Уверенность оценки ИИ">
                        {confidenceLabel}
                    </span>
                )}
                <time className="doctor-notification-card__time" dateTime={alert.created_at}>
                    {timeLabel}
                </time>
            </div>
            <h3 className="doctor-notification-card__title">{alert.title}</h3>

            {primarySummary && (
                <p
                    className={`doctor-notification-card__summary ${compact ? "doctor-notification-card__summary--clamp" : ""}`}
                >
                    {primarySummary}
                </p>
            )}

            {!compact && doctorNote && (
                <p className="doctor-notification-card__doctor-note">
                    <span className="doctor-notification-card__doctor-note-label">Для врача:</span>{" "}
                    {doctorNote}
                </p>
            )}

            {!compact && evidence.length > 0 && (
                <div className="doctor-notification-card__evidence">
                    <span className="doctor-notification-card__evidence-label">Признаки:</span>
                    <ul className="doctor-notification-card__evidence-list">
                        {evidence.map((item, idx) => (
                            <li key={`${idx}-${item}`}>{item}</li>
                        ))}
                    </ul>
                </div>
            )}

            {showLegacyMessageOnly && (
                <p className="doctor-notification-card__message doctor-notification-card__message--legacy">
                    {alert.message}
                </p>
            )}

            <div className="doctor-notification-card__actions">
                {onOpenPatient && (
                    <button
                        type="button"
                        className="doctor-notification-btn doctor-notification-btn--ghost"
                        onClick={() => onOpenPatient(alert)}
                    >
                        К пациенту
                    </button>
                )}
                {!alert.is_read && onMarkRead && (
                    <button
                        type="button"
                        className="doctor-notification-btn doctor-notification-btn--primary"
                        disabled={markingRead}
                        onClick={() => onMarkRead(alert)}
                    >
                        {markingRead ? "…" : "Прочитано"}
                    </button>
                )}
            </div>
        </article>
    );
}
